import React, { useState } from 'react';
import Modal from '../Modal';
import { saudeService } from '../../services/saude.service';
import {
  CheckCircleIcon,
  XCircleIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import type { ColaboradorAtestadoContract } from '@sstexames/shared-types';

interface DecisaoAbonoModalProps {
  isOpen: boolean;
  onClose: () => void;
  atestado: ColaboradorAtestadoContract | null;
  onSuccess: () => void;
  toast: {
    success: (msg: string) => void;
    error: (msg: string) => void;
  };
}

export default function DecisaoAbonoModal({
  isOpen,
  onClose,
  atestado,
  onSuccess,
  toast,
}: DecisaoAbonoModalProps) {
  const [acao, setAcao] = useState<'ABONO_ACEITO' | 'ABONO_RECUSADO'>('ABONO_ACEITO');
  const [diasAbonados, setDiasAbonados] = useState<number>(
    atestado?.dias_afastamento || 1,
  );
  const [motivo, setMotivo] = useState('');
  const [submitting, setSubmitting] = useState(false);

  React.useEffect(() => {
    if (atestado) {
      setDiasAbonados(atestado.dias_afastamento || 1);
      setMotivo('');
    }
  }, [atestado]);

  if (!atestado) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await saudeService.decidirAbono({
        atestado_id: atestado.id,
        acao,
        motivo: motivo || undefined,
        dias_abonados: acao === 'ABONO_ACEITO' ? diasAbonados : 0,
      });

      toast.success(
        acao === 'ABONO_ACEITO'
          ? `Abono de ${diasAbonados} dia(s) aprovado para o colaborador.`
          : 'Abono recusado com registro de justificativa de RH.',
      );
      onSuccess();
      onClose();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Falha ao registrar decisão de abono.';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Decisão Administrativa de Abono (RH)"
      size="md"
    >
      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Resumo do Atestado */}
        <div className="bg-gray-50 border border-gray-200/80 rounded-xl p-3.5 space-y-1.5 text-xs text-gray-700">
          <div className="flex justify-between font-semibold text-gray-900">
            <span>Colaborador:</span>
            <span>{atestado.colaborador_nome || `#${atestado.colaborador_id}`}</span>
          </div>
          <div className="flex justify-between">
            <span>Período Solicitado:</span>
            <span>
              {new Date(atestado.data_inicio).toLocaleDateString('pt-BR')} (
              {atestado.dias_afastamento} dias)
            </span>
          </div>
          {atestado.motivo_cid ? (
            <div className="flex justify-between">
              <span>CID-10:</span>
              <span className="font-semibold text-indigo-700">
                {atestado.motivo_cid} {atestado.motivo_cid_descricao ? `• ${atestado.motivo_cid_descricao}` : ''}
              </span>
            </div>
          ) : null}
          {atestado.medico_nome ? (
            <div className="flex justify-between">
              <span>Médico / Emissor:</span>
              <span>
                {atestado.medico_nome} {atestado.medico_crm ? `(CRM ${atestado.medico_crm}/${atestado.medico_uf})` : ''}
              </span>
            </div>
          ) : null}
        </div>

        {/* Escolha da Ação */}
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-2">
            Decisão para a Folha e Ponto <span className="text-red-500">*</span>
          </label>
          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => setAcao('ABONO_ACEITO')}
              className={`p-3 rounded-xl border flex flex-col items-center justify-center gap-1.5 transition text-xs font-bold ${
                acao === 'ABONO_ACEITO'
                  ? 'border-emerald-600 bg-emerald-50 text-emerald-800 ring-2 ring-emerald-500/20 shadow-xs'
                  : 'border-gray-200 hover:border-gray-300 text-gray-600 bg-white'
              }`}
            >
              <CheckCircleIcon className="w-5 h-5 text-emerald-600" />
              <span>Aprovar Abono</span>
            </button>

            <button
              type="button"
              onClick={() => setAcao('ABONO_RECUSADO')}
              className={`p-3 rounded-xl border flex flex-col items-center justify-center gap-1.5 transition text-xs font-bold ${
                acao === 'ABONO_RECUSADO'
                  ? 'border-rose-600 bg-rose-50 text-rose-800 ring-2 ring-rose-500/20 shadow-xs'
                  : 'border-gray-200 hover:border-gray-300 text-gray-600 bg-white'
              }`}
            >
              <XCircleIcon className="w-5 h-5 text-rose-600" />
              <span>Recusar Abono</span>
            </button>
          </div>
        </div>

        {/* Quantidade de Dias Abonados (quando aceito) */}
        {acao === 'ABONO_ACEITO' && (
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">
              Dias a Abonar na Folha <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              min={1}
              max={atestado.dias_afastamento || 365}
              value={diasAbonados}
              onChange={(e) => setDiasAbonados(parseInt(e.target.value, 10) || 1)}
              required
              className="w-full text-xs sm:text-sm border border-gray-300 rounded-lg px-3 py-2 bg-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500 shadow-xs"
            />
            <p className="text-[11px] text-gray-500 mt-1">
              Permite abono parcial se determinado pela política interna da empresa.
            </p>
          </div>
        )}

        {/* Justificativa / Parecer RH */}
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-1">
            Justificativa / Parecer Administrativo
          </label>
          <textarea
            rows={2}
            placeholder={
              acao === 'ABONO_ACEITO'
                ? 'Ex: Atestado válido conferido conforme política interna de benefícios.'
                : 'Ex: Documento sem carimbo médico legível ou prazo legal expirado.'
            }
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            className="w-full text-xs sm:text-sm border border-gray-300 rounded-lg px-3 py-2 bg-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500 shadow-xs"
          />
        </div>

        {/* Rodapé de Ações */}
        <div className="flex items-center justify-end gap-3 pt-3 border-t border-gray-200">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 rounded-lg text-xs font-semibold text-gray-700 hover:bg-gray-100 transition"
          >
            Voltar
          </button>
          <button
            type="submit"
            disabled={submitting}
            className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-white text-xs font-bold shadow-sm transition disabled:opacity-50 ${
              acao === 'ABONO_ACEITO'
                ? 'bg-emerald-600 hover:bg-emerald-700'
                : 'bg-rose-600 hover:bg-rose-700'
            }`}
          >
            {submitting ? (
              <ArrowPathIcon className="w-4 h-4 animate-spin" />
            ) : acao === 'ABONO_ACEITO' ? (
              <CheckCircleIcon className="w-4 h-4" />
            ) : (
              <XCircleIcon className="w-4 h-4" />
            )}
            {submitting ? 'Salvando...' : 'Confirmar Decisão'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
